/** @jsxImportSource @emotion/react */

import { ReactNode, createContext, useState, useCallback, useContext, useEffect } from "react";
import { useHistory } from "react-router-dom";

import { css } from "@emotion/react";
import { Snackbar } from "@material-ui/core";
import { AlertProps, Alert } from "@material-ui/lab";

interface Opts {
  type?: AlertProps["severity"];
  variant?: "short" | "long" | "permanent";
  closeable?: boolean;
}

type ContextProps = (message: string, options?: Opts) => void;

interface ProviderProps {
  children: ReactNode;
}

const ToastContext = createContext<ContextProps>(() => null);
const durationMap: Record<string, any> = {
  long: 4000,
  short: 2000,
};

const ToastProvider = ({ children }: ProviderProps): JSX.Element => {
  const { listen } = useHistory();
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState("");
  const [severity, setsSeverity] = useState<AlertProps["severity"]>("success");
  const [durationType, setDurationType] = useState("long");
  const [closeBtn, setCloseBtn] = useState(false);

  const showToast = useCallback((message, { type, variant, closeable } = {}) => {
    setOpen(true);
    setMsg(message);
    if (type) setsSeverity(type);
    if (variant) setDurationType(variant);
    if (closeable || variant === "permanent") setCloseBtn(true);
  }, []);

  const handleClose = useCallback(() => setOpen(false), []);

  const reset = useCallback(() => {
    setsSeverity("success");
    setDurationType("long");
    setCloseBtn(false);
  }, []);

  const value = useCallback(showToast, [showToast]);

  useEffect(() => {
    const unlisten = listen(() => {
      if (open) handleClose();
    });

    return (): void => unlisten();
  }, [listen, open, handleClose]);

  return (
    <ToastContext.Provider value={value}>
      <Snackbar
        open={open}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        autoHideDuration={durationMap[durationType]}
        onClose={handleClose}
        onExited={reset}
      >
        <Alert
          // TODO: overrides by theme when supported
          css={css`
            .MuiAlert-icon {
              display: none;
            }
          `}
          variant="filled"
          severity={severity}
          onClose={closeBtn ? handleClose : undefined}
        >
          {msg}
        </Alert>
      </Snackbar>
      {children}
    </ToastContext.Provider>
  );
};

const useToast = (): ContextProps => useContext(ToastContext);

export { ToastProvider, useToast };
